import * as React from 'react';
import { View, Text, Image } from '@react-pdf/renderer';
import { commonPDFStyles } from '../styles/pdfStyles';
import { typographyStyles, specializedTypography } from '../styles/typography';
import { formatDateForPDF } from '../utils/dataFormatters';
import type { BillPdfLayout } from '../styles/billPdfLayouts';

/**
 * PDF Header Component
 * 
 * Replaces HeaderDocument component with @react-pdf/renderer equivalent.
 * Handles hospital branding, logo, and contact information.
 */

interface PDFHeaderProps {
  hospitalName: string;
  logoBlob: string;
  locale: string;
  address: string;
  city: string;
  email: string;
  phone: string;
  size?: 'A4' | 'ticket';
  title?: string | React.ReactNode;
  /** Billing PDF preset (summaryCompact uses tighter spacing). */
  headerLayout?: BillPdfLayout['header'];
}

export const PDFHeader: React.FC<PDFHeaderProps> = ({
  hospitalName,
  logoBlob,
  locale,
  address,
  city,
  email,
  phone,
  size = 'A4',
  title,
  headerLayout,
}) => {
  const logoBase64 = logoBlob || '';

  if (size !== 'A4') {
    return null;
  }

  const logoStyle = headerLayout
    ? { width: headerLayout.logoWidth, height: headerLayout.logoHeight, objectFit: 'contain' as const }
    : commonPDFStyles.logo;

  const titleTypography = headerLayout?.titleStyle === 'h4' ? typographyStyles.h4 : typographyStyles.h3;

  return (
    <View style={[commonPDFStyles.header, { flexDirection: 'column', marginBottom: headerLayout?.marginBottom }]}>
      <View style={{ flexDirection: 'row', width: '100%' }}>
        <View style={[commonPDFStyles.headerLeft, { alignItems: 'flex-start'}]}>
          {logoBase64 && (
            <Image
              src={logoBase64}
              style={logoStyle}
            />
          )}
        </View>

        <View style={commonPDFStyles.headerRight}>
          <Text style={[specializedTypography.hospitalName, { textAlign: 'right' }]}>
            {hospitalName}
          </Text>

          <Text style={[specializedTypography.hospitalAddress, { textAlign: 'right' }]}>
            {address}
          </Text>

          <Text style={[specializedTypography.hospitalAddress, { textAlign: 'right' }]}>
            {phone}
          </Text>

          <Text style={[specializedTypography.hospitalAddress, { textAlign: 'right' }]}>
            {email}
          </Text>

          <View style={{ marginTop: headerLayout?.dateMarginTop ?? 16 }}>
            <Text style={[specializedTypography.hospitalAddress, { textAlign: 'right' }]}>
              {city}, le {formatDateForPDF(new Date(), locale)}
            </Text>
          </View>
        </View>
      </View>

      {title && (
        <View style={{ width: '100%', marginTop: headerLayout?.titleMarginTop ?? 2 }}>
          <Text style={[titleTypography, { textAlign: 'center' }]}>
            {typeof title === 'string' ? title : String(title)}
          </Text>
        </View>
      )}
    </View>
  );
};

export default PDFHeader;
